import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Star, BookMarked, Sparkles, Check } from 'lucide-react'
import { useProModalStore } from '../../stores/proModalStore'
import { useStartCheckout } from '../../hooks/useBilling'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'

const FEATURE_ICONS = [
  <Star size={16} key="star" />,
  <BookMarked size={16} key="book" />,
  <Sparkles size={16} key="sparkles" />,
  <Check size={16} key="check" />,
]

export default function ProUpgradeModal() {
  const { isOpen, close } = useProModalStore()
  const navigate = useNavigate()
  const overlayRef = useRef<HTMLDivElement>(null)
  const { mutate: startCheckout, isPending } = useStartCheckout()
  const { tr } = useLocale()
  const pu = tr.proUpgrade

  // ADR-106 P3: no top-level swipe while the upgrade modal is open.
  useSwipeNavBlock(isOpen, 'pro-upgrade-modal')

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, close])

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) close()
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-modal-title"
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 20,
          width: '100%',
          maxWidth: 440,
          position: 'relative',
          boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          border: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          // 100dvh accounts for mobile browser chrome; keeps close button reachable on short/landscape viewports
          maxHeight: 'calc(100dvh - 32px)',
          overflow: 'hidden',
        }}
      >
        {/* Close button — outside the scroll container so it is always reachable */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 12px 0', flexShrink: 0 }}>
          <button
            onClick={close}
            aria-label={pu.close}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: 6,
              borderRadius: 8,
              display: 'flex',
              lineHeight: 1,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content — overflows internally, never clips the close button */}
        <div style={{ overflowY: 'auto', padding: '8px 32px 32px', flex: 1 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 14px',
            background: 'var(--pro-gradient)',
            borderRadius: 50,
            marginBottom: 12,
          }}>
            <Star size={13} color="#fff" fill="#fff" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.06em' }}>
              CARDSMITH PRO
            </span>
          </div>

          <h2
            id="pro-modal-title"
            style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}
          >
            {pu.modalTitle}
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {pu.modalSubtitle}
          </p>
        </div>

        {/* Feature list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {pu.modalFeatures.map((f, i) => (
            <div
              key={f.title}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 14px',
                backgroundColor: 'var(--bg-page)',
                borderRadius: 10,
                border: '1px solid var(--border)',
              }}
            >
              <div style={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: 'rgba(245,158,11,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#F59E0B',
              }}>
                {FEATURE_ICONS[i]}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                  {f.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.desc}</div>

              </div>
            </div>
          ))}
        </div>

        {/* Pricing row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          padding: '0 4px',
        }}>
          <div>
            <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)' }}>{pu.price}</span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 4 }}>{pu.perMonth}</span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{pu.cancelAnytime}</span>
        </div>

        {/* CTA */}
        <button
          onClick={() => startCheckout()}
          disabled={isPending}
          style={{
            width: '100%',
            padding: '14px 0',
            background: 'var(--pro-gradient)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 700,
            cursor: isPending ? 'wait' : 'pointer',
            opacity: isPending ? 0.8 : 1,
            transition: 'opacity 0.15s',
            marginBottom: 10,
          }}
        >
          {isPending ? pu.redirectingShort : pu.getCta}
        </button>

        <button
          onClick={() => { close(); navigate('/pro') }}
          style={{
            width: '100%',
            padding: '10px 0',
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {pu.learnMore}
        </button>
        </div> {/* end scrollable content */}
      </div>
    </div>
  )
}
